import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ShieldCheck, Wrench, HardHat, Truck, Briefcase, Crown,
  ArrowRight, Activity, AlertTriangle, FileText, Languages,
  Building2, Zap, Radio, ChevronRight, Cog,
} from "lucide-react";
import { setAdminToken, clearAdminToken } from "@/lib/adminAuth";
import { setPmToken, clearPmToken } from "@/lib/pmAuth";
import { setShopToken, clearShopToken } from "@/lib/shopAuth";
import MobileFieldShowcase from "@/components/MobileFieldShowcase";
import TrainingHubShowcase from "@/components/TrainingHubShowcase";
import DemoCallout, { CalloutsToggle } from "@/components/DemoCallout";
import DemoAutoTour, { AutoTourLaunchButton } from "@/components/DemoAutoTour";
import LiveHeartbeatTicker from "@/components/LiveHeartbeatTicker";
import LiveOperationsPreview from "@/components/LiveOperationsPreview";
import FieldFirstWorkflows from "@/components/FieldFirstWorkflows";
import TryToolboxTalk from "@/components/TryToolboxTalk";
import OperationalActivityTicker from "@/components/OperationalActivityTicker";
import OperationalPressurePanel from "@/components/OperationalPressurePanel";
import WorkflowChainPanel from "@/components/WorkflowChainPanel";
import OperationalAutoTour from "@/components/OperationalAutoTour";
import { useTenant } from "@/lib/useTenant";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ROLE_META = {
  "admin@forgeops.demo":    { icon: Crown,        portal: "/exec",           title: "EXECUTIVE / OWNER",    tag: "01", domain: "Users · Routing · Records · System", blurb: "Owner-level oversight. Every job · every record · every alert.",          name: "Demo Admin" },
  "super@forgeops.demo":    { icon: ShieldCheck,  portal: "/super",          title: "SUPERINTENDENT",        tag: "02", domain: "Production · Crews · Weather · Inspections", blurb: "Field leadership view — every active jobsite at a glance.",                  name: "Demo Superintendent" },
  "pm@forgeops.demo":       { icon: Briefcase,    portal: "/pm",             title: "PROJECT MANAGER",       tag: "03", domain: "Reports · Approvals · RFIs · Escalations",   blurb: "Daily reports, RFIs, manpower, vendors — by job, by shift.",                  name: "Demo Project Manager" },
  "safety@forgeops.demo":   { icon: ShieldCheck,  portal: "/admin/incidents", title: "SAFETY DIRECTOR",       tag: "04", domain: "Incidents · Observations · Corrective · Training", blurb: "Incidents · near-misses · JHP review · OSHA-defensible records.",            name: "Demo Safety Manager" },
  "shop@forgeops.demo":     { icon: Wrench,       portal: "/shop",           title: "SHOP MANAGER",          tag: "05", domain: "OOS · Repairs · Downtime · Parts",            blurb: "Equipment failures · OOS clearance · parts on order.",                        name: "Demo Shop Manager" },
  "foreman@forgeops.demo":  { icon: HardHat,      portal: "/foreman",        title: "FOREMAN",               tag: "06", domain: "Daily Reports · JHP · Toolbox · Crew",         blurb: "Daily report, JHP ack, toolbox talk — straight from the truck.",             name: "Demo Foreman" },
  "operator@forgeops.demo": { icon: Truck,        portal: "/foreman",        title: "OPERATOR",              tag: "07", domain: "Pre-Op · Lockout · Parts · Walkaround",        blurb: "Pre-Op walk-around · OOS lockout · parts request.",                          name: "Demo Operator" },
};

// Default seeded users — used INSTANTLY so the role tiles render on first
// paint. Live API data enhances/replaces but never blocks rendering.
const DEFAULT_SEED_USERS = Object.entries(ROLE_META).map(([email, m]) => ({
  email,
  name: m.name,
  role: "demo",
  portal: m.title,
}));

export default function DemoLanding() {
  const navigate = useNavigate();
  const tenant = useTenant();
  const { t } = useT();
  // Default state populated INSTANTLY so the role tiles render on first
  // paint — never blocked by the API. Live API enriches on arrival.
  const [state, setState] = useState({ seed_users: DEFAULT_SEED_USERS });
  const [busyEmail, setBusyEmail] = useState(null);
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    clearAdminToken(); clearPmToken(); clearShopToken();
    let cancel = false;
    Promise.all([
      axios.get(`${API}/demo/public/state`).catch(() => null),
      axios.get(`${API}/demo/activity-feed`).catch(() => null),
      axios.get(`${API}/demo/folders`).catch(() => null),
      axios.get(`${API}/jobs`).catch(() => null),
      axios.get(`${API}/equipment-master`).catch(() => null),
    ]).then(([s, f, fl, j, e]) => {
      if (cancel) return;
      // Only override defaults if the API actually returned users.
      const apiUsers = s?.data?.seed_users;
      if (Array.isArray(apiUsers) && apiUsers.length > 0) {
        setState({ ...(s.data || {}), seed_users: apiUsers });
      }
      setFeed(f?.data?.events || []);
      const equipment = e?.data?.items || [];
      setStats({
        jobs: (j?.data?.items || []).length,
        equipment_total: equipment.length,
        equipment_oos: equipment.filter((u) => u.out_of_service === "Yes" || u.out_of_service === true).length,
        folders: fl?.data?.folders || [],
        feed_total: f?.data?.count || 0,
      });
    });
    return () => { cancel = true; };
  }, []);

  const enterAs = async (user) => {
    setBusyEmail(user.email);
    try {
      clearAdminToken(); clearPmToken(); clearShopToken();
      const r = await axios.post(`${API}/demo/login-as`, { email: user.email });
      const tk = r?.data?.tokens || {};
      if (tk.admin_token) setAdminToken(tk.admin_token);
      if (tk.pm_token)    setPmToken(tk.pm_token);
      if (tk.shop_token)  setShopToken(tk.shop_token);
      try { localStorage.setItem("forgeops.demo.identity", JSON.stringify(user)); } catch { /* */ }
      toast.success(`Signed in as ${user.name}`);
      navigate(ROLE_META[user.email]?.portal || "/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Sign-in failed`);
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "#0A0A0A" }}>
      {/* Cinematic auto-tour overlay (Shift+T to start, ESC to exit) */}
      <DemoAutoTour />
      {/* Operational auto-tour — idle-triggered cinematic causality loop.
          Fires the ecosystem chain (ticker → pressure → chain → ledger
          → exec heat-map → archive) without giant arrows or onboarding
          chrome. Restrained mono telemetry strip only. */}
      <OperationalAutoTour />
      {/* Caution stripe */}
      <div className="caution-stripe" />
      {/* Live operational heartbeat — proves the system is alive on first paint */}
      <LiveHeartbeatTicker />

      {/* HEADER STRIP — horizontal logo */}
      <header className="border-b" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/demo" className="flex items-center gap-3" data-testid="demo-logo-link">
            <img
              src={tenant.logo_horizontal || "/forgedops-horizontal.png"}
              alt={tenant.brand_name || "FORGEDOPS"}
              className="h-10 sm:h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "#BDBDBD" }}>
            <CalloutsToggle />
            <span className="inline-flex w-2 h-2 rounded-full animate-pulse" style={{ background: "#FF6A00" }} />
            {t("Live Demo · DEMO_MODE Active")}
          </div>
        </div>
      </header>

      {/* HERO — cinematic command center splash */}
      <section
        className="relative overflow-hidden border-b"
        style={{
          borderColor: "#1A1A1A",
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(255,106,0,0.18), transparent 60%), radial-gradient(ellipse at 80% 80%, rgba(255,106,0,0.08), transparent 50%), #0A0A0A",
        }}
      >
        <div className="absolute inset-0 blueprint-bg opacity-30 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #FF6A00, transparent)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-4 inline-flex items-center gap-2" style={{ color: "#FF6A00" }}>
              <span className="inline-flex w-1.5 h-1.5 rounded-full" style={{ background: "#FF6A00" }} />
              {tenant.brand_name || "FORGEDOPS"} · {t("LIVE DEMO ENVIRONMENT")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[0.95] mb-5">
              {t("Run the work.")}<br />
              <span style={{ color: "#FF6A00" }}>{t("Not just manage it.")}</span>
            </h1>
            <p className="text-base sm:text-lg max-w-xl leading-relaxed" style={{ color: "#BDBDBD" }}>
              {t("An interactive operating ecosystem already running for a heavy civil contractor. Pick a role. The system signs you in. Every action persists. Every submission routes. No password walls.")}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#roles"
                className="inline-flex items-center gap-2 fo-btn-primary px-5 py-3 text-[12px]"
                data-testid="demo-cta-pickrole"
              >
                {t("Pick a role · enter system")} <ArrowRight className="w-4 h-4" />
              </a>
              <AutoTourLaunchButton />
              <a
                href="#workflows"
                className="inline-flex items-center gap-2 fo-btn-ghost px-5 py-3 text-[12px]"
              >
                {t("See live workflows")}
              </a>
            </div>

            {/* Live KPI strip */}
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px" style={{ background: "#1A1A1A" }}>
              <KpiTile label={t("Active Jobs")}      value={stats?.jobs ?? "—"}                 testId="demo-kpi-jobs" />
              <KpiTile label={t("Equipment · OOS")}  value={`${stats?.equipment_oos ?? "—"}/${stats?.equipment_total ?? "—"}`}  accent="#D32F2F" testId="demo-kpi-oos" />
              <KpiTile label={t("Active Records")}   value={feed.length || "—"}                 testId="demo-kpi-records" />
              <KpiTile label={t("Auto-routed")}      value="100%"                                accent="#FF6A00" testId="demo-kpi-routed" />
            </div>
          </div>

          {/* HERO RIGHT — vertical lockup, used with restraint */}
          <div className="lg:col-span-5 hidden lg:flex justify-end">
            <div className="relative">
              <div
                className="absolute -inset-10 rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(closest-side, rgba(255,106,0,0.35), transparent 70%)",
                  filter: "blur(20px)",
                }}
              />
              <img
                src={tenant.logo_vertical || "/forgedops-vertical.png"}
                alt={tenant.brand_name || "FORGEDOPS"}
                className="relative h-[420px] w-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Marquee — operational disciplines (matches forgedopshq.com) */}
        <div className="border-t border-b" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
          <div className="overflow-hidden py-2.5">
            <div className="flex gap-6 whitespace-nowrap animate-marquee font-mono text-[11px] uppercase tracking-[0.25em]" style={{ color: "#BDBDBD" }}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <span key={idx} className="flex gap-6">
                  {["Heavy Civil", "DOT", "FAA Airfield", "Earthwork", "Utilities",
                    "Asphalt", "Concrete", "Excavation", "GPS Machine Control",
                    "OSHA 10 & 30", "Trench Safety", "Fall Protection",
                    "Crane & Rigging", "Silica · Table 1", "FOD / RSA Discipline",
                    "Field Leadership"].map((d) => (
                    <span key={d} className="inline-flex items-center gap-6">
                      <span>{d}</span>
                      <span style={{ color: "#FF6A00" }}>◆</span>
                    </span>
                  ))}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPERATIONAL ACTIVITY TICKER — Bloomberg-NOC style live event rail.
          Establishes operational tension immediately under the hero. */}
      <section className="py-6" style={{ background: "#0A0A0A", borderTop: "1px solid #161616" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <OperationalActivityTicker />
        </div>
      </section>

      {/* LIVE OPERATIONS PREVIEW — cinematic operational heartbeat above role tiles */}
      <section className="py-8 sm:py-10" style={{ background: "#0A0A0A" }}>
        <LiveOperationsPreview />
      </section>

      {/* OPERATIONAL PRESSURE — concrete operational tension. Counts that
          would be on a contractor's mind at 7am: incidents, lockouts,
          overdue approvals, manpower gaps, expired certs, open CAs. */}
      <section className="py-8 sm:py-10" style={{ background: "#0A0A0A", borderTop: "1px solid #161616" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <OperationalPressurePanel />
        </div>
      </section>

      {/* FIELD-FIRST WORKFLOWS — simplicity counterweight to the cinematic preview.
          Premium outside, brutally simple inside. The crews-actually-use-this story. */}
      <div style={{ background: "#080808", borderTop: "1px solid #161616", borderBottom: "1px solid #161616" }}>
        <FieldFirstWorkflows />
      </div>

      {/* TRY-IT INTERACTIVE WORKFLOW — Toolbox Talk QR Flow. Real submission to
          /api/meetings; live RoutingAnimation overlay; ~15s field-realistic. */}
      <div style={{ background: "#0A0A0A", borderBottom: "1px solid #161616" }}>
        <TryToolboxTalk />
      </div>

      {/* WORKFLOW CHAIN PANEL — the core operational magic. Shows how a
          single submission cascades through the whole platform. Three real
          chains: Daily Report · Pre-Op Fail · Incident. Restrained design
          — connected nodes, directional arrows, no arcade animation. */}
      <section className="py-10 sm:py-14" style={{ background: "#0A0A0A", borderBottom: "1px solid #161616" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("System-wide routing")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white">
              {t("The platform reacts. Every submission. Every time.")}
            </h2>
            <p className="text-sm sm:text-base mt-2 leading-relaxed max-w-2xl" style={{ color: "#BDBDBD" }}>
              {t("No isolated modules. Forms route. PDFs render. KPIs roll forward. Archives lock. The same record is visible — to the right person, at the right level — within seconds.")}
            </p>
          </div>
          <WorkflowChainPanel />
        </div>
      </section>

      {/* ROLE TILES — cinematic, dense, ordered */}
      <section id="roles" className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("01 · ENTER THE SYSTEM")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
              {t("Pick a role.")}{" "}
              <span style={{ color: "#FF6A00" }}>{t("Run the system.")}</span>
            </h2>
            <p className="text-sm mt-2 max-w-2xl" style={{ color: "#BDBDBD" }}>
              {t("Seven seeded operating roles. One click, no passwords. Each role lands in the console where it lives — with seeded jobs, equipment, and crews already wired.")}
            </p>
          </div>
          <DemoCallout
            tag={t("HOW THIS WORKS")}
            title={t("Passwordless demo by design")}
            description="Each tile mints all three portal tokens (Admin / PM / Shop) so prospects can move freely. Real deployments use SSO, role-scoped JWTs, brute-force throttling, and audit logging."
            role="All 7 demo identities · 1-click sign-in"
            routes={["Admin", "PM", "Shop"]}
            anchor="tr"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded inline-flex items-center gap-2" style={{ background: "rgba(255,106,0,0.14)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.4)" }}>
              <span className="inline-flex w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#FF6A00" }} />
              {t("7 roles · no passwords")}
            </span>
          </DemoCallout>
        </div>

        {!state ? null : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "#1A1A1A" }}>
            {(state?.seed_users || []).map((u) => {
              const meta = ROLE_META[u.email];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <button
                  key={u.email}
                  onClick={() => enterAs(u)}
                  disabled={busyEmail === u.email}
                  className="group relative text-left p-5 sm:p-6 transition disabled:opacity-50 hover:bg-[#141414]"
                  style={{ background: "#101010" }}
                  data-testid={`demo-role-${u.email.split("@")[0]}`}
                >
                  <div
                    className="absolute inset-x-0 top-0 h-px transition-all"
                    style={{ background: busyEmail === u.email ? "#FF6A00" : "transparent" }}
                  />
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="inline-flex items-center justify-center w-11 h-11 rounded-md"
                      style={{
                        background: "rgba(255,106,0,0.12)",
                        color: "#FF6A00",
                        border: "1px solid rgba(255,106,0,0.3)",
                      }}
                    >
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#8A8A8A" }}>
                      {meta.tag}
                    </span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1" style={{ color: "#FF6A00" }}>
                    {meta.title}
                  </div>
                  <div className="font-display text-lg font-black text-white mb-2">
                    {u.name}
                  </div>
                  <div className="text-xs leading-snug mb-3" style={{ color: "#BDBDBD" }}>
                    {meta.blurb}
                  </div>
                  {/* Domain capability strip — what THIS role lives in */}
                  <div className="font-mono text-[9px] uppercase tracking-[0.18em] py-1.5 px-2 rounded mb-1" style={{ background: "rgba(255,106,0,0.08)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.2)" }}>
                    {meta.domain}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: "1px solid #1A1A1A" }}>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#8A8A8A" }}>
                      {u.email}
                    </span>
                    {busyEmail === u.email ? (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#FF6A00" }} />
                    ) : (
                      <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" style={{ color: "#FF6A00" }} />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* LIVE ACTIVITY TICKER — proves it's a live system */}
      <section className="border-t" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
          <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
                02 · LIVE OPERATIONS
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">
                Real activity.{" "}
                <span style={{ color: "#FF6A00" }}>Right now.</span>
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <DemoCallout
                tag="WHAT THIS IS"
                title="Unified ops feed"
                description="Every Daily Report, Pre-Op, Incident, Safety Meeting, and QA/QC submission lands here in real time. Severity color-codes itself. Crews and PMs see the same heartbeat."
                routes={["Field crews", "PM", "Safety Director", "Admin"]}
                alerts={["Email · push · in-app"]}
                anchor="tr"
              >
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#BDBDBD" }}>
                  <span className="inline-flex w-2 h-2 rounded-full animate-pulse" style={{ background: "#2E7D32" }} />
                  {stats?.feed_total || feed.length} events · last 24 hr
                </span>
              </DemoCallout>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {feed.slice(0, 6).map((e, i) => (
              <ActivityCard key={i} event={e} />
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOWS SECTION — what happens after submit */}
      <section id="workflows" className="border-t" style={{ borderColor: "#1A1A1A", background: "#0A0A0A" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("03 · CAUSE → EFFECT → CONSEQUENCE")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-3xl">
              {t("Submit anything.")}{" "}
              <span style={{ color: "#FF6A00" }}>{t("Watch the system act on it.")}</span>
            </h2>
            <p className="text-sm mt-3 max-w-2xl" style={{ color: "#BDBDBD" }}>
              {t("Every form is wired into the operational chain. Submissions don't sit in an inbox — they trigger lockouts, alerts, PDF generation, and folder routing in real time.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "#1A1A1A" }}>
            <WorkflowCard
              tag={t("DOCUMENTS")}
              title={t("Daily Report submitted")}
              chain={[
                t("Foreman taps SUBMIT on iPad"),
                t("PM auto-notified by email"),
                t("Branded PDF generated + signed"),
                t("Routed to PM Folder · Job Folder · Admin Records · Backup"),
              ]}
              testId="wf-daily"
            />
            <WorkflowCard
              tag={t("IDENTIFIES")}
              title={t("Pre-Op FAIL on equipment")}
              chain={[
                t("Operator marks brake check FAIL"),
                t("Unit instantly flagged OUT-OF-SERVICE"),
                t("Shop Queue receives alert + work order"),
                t("Equipment row turns RED in Ops Command View"),
              ]}
              testId="wf-preop"
            />
            <WorkflowCard
              tag={t("DOCUMENTS")}
              title={t("Incident report submitted")}
              chain={[
                t("Field crew documents incident with photos"),
                t("Severity tier auto-classified"),
                t("Safety Director + PM alerted"),
                t("Routed to Safety Folder · Admin · Insurance log"),
              ]}
              testId="wf-incident"
            />
            <WorkflowCard
              tag={t("TRACKS")}
              title={t("QA/QC concrete pour signed off")}
              chain={[
                t("QC manager runs 13-point checklist"),
                t("Mix design + yards + vendor stamped"),
                t("Min-3 photos enforced · digital signature"),
                t("Routed to PM · Admin · Job Folder + Test-Lab cc"),
              ]}
              testId="wf-qaqc"
            />
          </div>
        </div>
      </section>

      {/* ADOPTION — bilingual + white-label (matches forgedopshq.com) */}
      <section className="border-t" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("04 · BUILT FOR REAL FIELD ADOPTION")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-3xl">
              {t("If crews can't use it,")}{" "}
              <span style={{ color: "#FF6A00" }}>{t("it doesn't run the work.")}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <AdoptionPanel
              tag={t("ADOPTION 01")}
              icon={Languages}
              title={t("Bilingual systems crews can use.")}
              copy={t("Fully bilingual EN / ES workflows — not Google-translated, but field-tested for adoption. Every form, every report, every training video toggles instantly.")}
              bullets={[
                t("Bilingual forms & inspections"),
                t("Bilingual safety & training content"),
                t("Bilingual onboarding & instructions"),
                t("Per-user language toggle"),
              ]}
              testId="adoption-bilingual"
            />
            <AdoptionPanel
              tag={t("ADOPTION 02")}
              icon={Building2}
              title={t("Your company. Your system.")}
              copy={t("Crews adopt systems faster when the platform looks like their own company. Every ForgedOps deployment is white-labeled — your brand, your colors, your terminology.")}
              bullets={[
                t("Customer-branded portals"),
                t("Custom forms, workflows & approval paths"),
                t("Custom training content & libraries"),
                t("Branded PDFs, reports & notifications"),
              ]}
              testId="adoption-whitelabel"
            />
          </div>
        </div>
      </section>

      {/* MODULAR PLATFORM POSITIONING — "ForgedOps becomes your operational
          backbone." Restrained enterprise band placed between Adoption and
          Chaos→Control so prospects internalize the infrastructure framing
          BEFORE the chaos lift — without breaking the visual rhythm. */}
      <section className="border-t" style={{ borderColor: "#1A1A1A", background: "#0A0A0A" }} data-testid="modular-platform-band">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("05 · BASE OPERATIONAL INFRASTRUCTURE")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-4xl leading-tight">
              {t("ForgedOps becomes your")}{" "}
              <span style={{ color: "#FF6A00" }}>{t("operational backbone.")}</span>
            </h2>
            <p className="text-base sm:text-lg mt-4 max-w-3xl leading-relaxed" style={{ color: "#BDBDBD" }}>
              {t("One platform — not seven disconnected apps. Field, shop, safety, management, executive, compliance, training, documentation, communication, routing, reporting, and analytics — unified into a single operational ecosystem custom-built around your workflow.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "#1A1A1A" }}>
            {[
              {
                tag: t("BASE"),
                title: t("What ships day-one"),
                copy: t("Daily reports · incidents · inspections · meetings · equipment · QA/QC · routing · PDFs · executive dashboards · bilingual training — all wired to a real-time operational event bus."),
                color: "#FF6A00",
              },
              {
                tag: t("CUSTOM"),
                title: t("Built around your workflow"),
                copy: t("Custom forms, approval chains, escalation rules, role hubs, dashboards, and integrations — modeled on how your company actually operates, not what fits a template."),
                color: "#FFB000",
              },
              {
                tag: t("EXTEND"),
                title: t("Modular by design"),
                copy: t("Add modules over time — fleet GPS, payroll integration, ERP sync, vendor portals, owner reporting, e-tickets, machine telemetry — every new piece speaks the same operational event bus."),
                color: "#65DA76",
              },
            ].map((b, i) => (
              <div key={i} className="p-6 sm:p-7" style={{ background: "#0F0F0F" }}>
                <span
                  className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold"
                  style={{ color: b.color }}
                >
                  {b.tag}
                </span>
                <div className="mt-3 font-display text-lg font-bold text-white tracking-tight">
                  {b.title}
                </div>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "#BDBDBD" }}>
                  {b.copy}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px" style={{ background: "#1A1A1A" }}>
            {[
              [t("UNIFIED"),         t("One event bus · one source of truth")],
              [t("WHITE-LABEL"),     t("Your brand · your terminology")],
              [t("INTEGRABLE"),      t("ERP · payroll · GPS · sensors")],
              [t("ENTERPRISE-GRADE"),t("Audit trails · role-scoped · SOC-ready")],
            ].map(([tag, line], i) => (
              <div key={i} className="p-4 sm:p-5" style={{ background: "#0B0B0B" }}>
                <span
                  className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold"
                  style={{ color: "#FF6A00" }}
                >
                  {tag}
                </span>
                <div className="mt-1.5 font-mono text-[11px] leading-snug" style={{ color: "#BDBDBD" }}>
                  {line}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHAOS → CONTROL strip — direct lift from website language */}
      <section className="border-t" style={{ borderColor: "#1A1A1A", background: "#0A0A0A" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
              {t("06 · FROM CHAOS TO OPERATIONAL CONTROL")}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight max-w-3xl">
              {t("Same field. Same crews.")}{" "}
              <span style={{ color: "#FF6A00" }}>{t("A system they actually trust.")}</span>
            </h2>
          </div>
          <div className="space-y-px" style={{ background: "#1A1A1A" }}>
            {[
              [t("Paperwork chaos in trucks"),            t("Field visibility on every shift")],
              [t("Crews bypassing systems"),              t("Workflows crews actually use")],
              [t("Office / field disconnect"),            t("Real-time operational alignment")],
              [t("Buried inspections & forms"),           t("Auto-routed sign-off + audit trail")],
              [t("Failed rollout adoption"),              t("Bilingual, brand-fit deployments")],
              [t("Accountability gaps"),                  t("Production traced to crew + shift")],
            ].map(([chaos, control], i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ background: "#1A1A1A" }}>
                <div className="p-5 flex items-center gap-3" style={{ background: "#0F0F0F" }}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#8A8A8A" }}>{t("Chaos")}</span>
                  <span className="text-sm" style={{ color: "#BDBDBD" }}>{chaos}</span>
                </div>
                <div className="p-5 flex items-center gap-3" style={{ background: "#0F0F0F" }}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF6A00" }}>{t("Control")}</span>
                  <span className="text-sm font-semibold text-white">{control}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIELD-FIRST MOBILE SHOWCASE (06) */}
      <MobileFieldShowcase />

      {/* TRAINING CENTER SHOWCASE (07) */}
      <TrainingHubShowcase />

      {/* GlobalFooter (mounted once in App.js) handles the bottom strip.
          No second footer here — keeps the page exit quiet and premium. */}
    </div>
  );
}

function KpiTile({ label, value, accent = "#FFFFFF", testId }) {
  return (
    <div className="px-4 py-3 sm:py-4" style={{ background: "#0F0F0F" }} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#BDBDBD" }}>{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-black" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ActivityCard({ event }) {
  const sevColor =
    event.severity === "critical" ? "#D32F2F" :
    event.severity === "warning"  ? "#FFD000" :
    event.severity === "success"  ? "#2E7D32" : "#FF6A00";
  const Icon =
    event.kind === "INCIDENT"        ? AlertTriangle :
    event.kind === "PRE-OP"          ? Truck :
    event.kind === "DAILY REPORT"    ? FileText :
    event.kind === "SAFETY MEETING"  ? HardHat :
    event.kind === "QA/QC"           ? ShieldCheck : Activity;
  return (
    <div className="p-4 border" style={{ background: "#101010", borderColor: "#1A1A1A" }} data-testid="demo-activity-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0" style={{ background: `${sevColor}22`, color: sevColor, border: `1px solid ${sevColor}55` }}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: sevColor }}>{event.kind}</span>
            {event.job_number && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
                {event.job_number}
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-white leading-tight truncate">{event.title}</div>
          {event.subtitle && <div className="text-xs mt-0.5 truncate" style={{ color: "#BDBDBD" }}>{event.subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function WorkflowCard({ tag, title, chain, testId }) {
  return (
    <div className="p-5 sm:p-6" style={{ background: "#0F0F0F" }} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 rounded" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
          {tag}
        </span>
        <Cog className="w-3.5 h-3.5 animate-spin-slow" style={{ color: "#FF6A00" }} />
      </div>
      <div className="font-display text-lg font-black mb-4 text-white">{title}</div>
      <ol className="space-y-2.5">
        {chain.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm" style={{ color: i === chain.length - 1 ? "#FFFFFF" : "#BDBDBD" }}>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full font-mono text-[10px] shrink-0" style={{ background: i === chain.length - 1 ? "#FF6A00" : "#1A1A1A", color: i === chain.length - 1 ? "#000" : "#FF6A00" }}>
              {i + 1}
            </span>
            <span className={i === chain.length - 1 ? "font-semibold" : ""}>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function AdoptionPanel({ tag, icon: Icon, title, copy, bullets, testId }) {
  return (
    <div className="p-6 sm:p-8 border" style={{ background: "#0F0F0F", borderColor: "#1A1A1A" }} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: "#8A8A8A" }}>{tag}</div>
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-md shrink-0" style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}>
          <Icon className="w-5 h-5" />
        </span>
        <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white leading-tight">{title}</h3>
      </div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: "#BDBDBD" }}>{copy}</p>
      <ul className="space-y-1.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm">
            <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: "#FF6A00" }} />
            <span style={{ color: "#FFFFFF" }}>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
